import React, { useState, useEffect } from 'react';
import {
  DocumentArrowDownIcon,
  WalletIcon,
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import ExcelJS from 'exceljs';
import ConfirmationModal from './ConfirmationModal';
import AttributeLabel from './AttributeLabel';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface Account {
  id: string | number;
  name: string;
  kontotyp?: string;
  beschreibung?: string;
  waehrung?: string;
  startbetrag?: number;
}

interface Statement {
  id: string | number;
  originalId?: string;
  konto_id: string | number;
  buchungstag: string;
  buchungstext?: string;
  name_zahlungsbeteiligter?: string;
  verwendungszweck?: string;
  betrag: number;
  documents?: any[];
}

interface ExcelExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: Account[];
  allStatements: Statement[];
  isMobile?: boolean;
  isLoading?: boolean;
}

const ExcelExportModal: React.FC<ExcelExportModalProps> = ({
  isOpen,
  onClose,
  accounts,
  allStatements,
  isMobile = false,
  isLoading = false
}) => {
  const [selectedModalAccount, setSelectedModalAccount] = useState<string | number | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [includeDocumentHyperlinks, setIncludeDocumentHyperlinks] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'error',
    title: '',
    message: '',
    variant: 'single' as 'single',
  });

  const availableYears = Array.from(new Set(allStatements.map((stmt) => new Date(stmt.buchungstag).getFullYear()))).sort((a, b) => b - a);

  const formatCurrency = (amount: number, currency = 'EUR') =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount || 0);

  const formatCurrencyCompact = (amount: number) => {
    const absAmount = Math.abs(amount);
    if (absAmount >= 1000) {
      return `${(amount / 1000).toFixed(1)}k`;
    } else if (absAmount >= 100) {
      return `${Math.round(amount)}€`;
    }
    return formatCurrency(amount);
  };

  const formatDateForDisplay = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });

  const allStatementsSorted = React.useMemo(() => {
    return [...allStatements].sort((a, b) => {
      const dateA = new Date(a.buchungstag);
      const dateB = new Date(b.buchungstag);
      if (dateA.getTime() === dateB.getTime()) {
        const idA: any = a.originalId === 'start' ? -1 : parseInt(a.originalId || '0', 10) || a.originalId || 0;
        const idB: any = b.originalId === 'start' ? -1 : parseInt(b.originalId || '0', 10) || b.originalId || 0;
        return idA < idB ? -1 : idA > idB ? 1 : 0;
      }
      return dateA.getTime() - dateB.getTime();
    });
  }, [allStatements]);

  const getCumulativeBalanceUpTo = (statement: Statement, accountId: string | number, statementsList: Statement[]) => {
    if (!statementsList || statementsList.length === 0) return 0;
    const index = statementsList.findIndex(
      (s) => s.id === statement.id && s.konto_id === accountId
    );
    if (index === -1) {
      const globalIndex = allStatementsSorted.findIndex(s => s.id === statement.id && s.konto_id === accountId);
      if (globalIndex === -1) return 0;
      return allStatementsSorted.slice(0, globalIndex + 1)
        .filter((s) => s.konto_id === accountId)
        .reduce((sum, stmt) => sum + (stmt.betrag || 0), 0);
    }
    return statementsList.slice(0, index + 1)
      .filter((s) => s.konto_id === accountId)
      .reduce((sum, stmt) => sum + (stmt.betrag || 0), 0);
  };

  const filteredModalStatements = allStatementsSorted.filter(
    (stmt) => {
      if (!selectedModalAccount) return false;
      const stmtKontoId = String(stmt.konto_id);
      const selectedId = String(selectedModalAccount);
      return stmtKontoId === selectedId;
    }
  );

  const filteredModalStatementsForYear = filteredModalStatements
    .filter((stmt) => new Date(stmt.buchungstag).getFullYear() === parseInt(selectedYear))
    .map((stmt) => ({
      ...stmt,
      saldo: getCumulativeBalanceUpTo(stmt, selectedModalAccount!, filteredModalStatements),
    }));

  // Calculate statistics for selected account and year
  const accountStats = React.useMemo(() => {
    const yearStatements = filteredModalStatementsForYear;
    const einnahmen = yearStatements.filter(s => s.betrag > 0).reduce((sum, s) => sum + s.betrag, 0);
    const ausgaben = yearStatements.filter(s => s.betrag < 0).reduce((sum, s) => sum + Math.abs(s.betrag), 0);
    const bilanz = einnahmen - ausgaben;
    const anzahl = yearStatements.length;
    return { einnahmen, ausgaben, bilanz, anzahl };
  }, [filteredModalStatementsForYear]);

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();

      // Stildefinitionen
      const FONT_FAMILY_MAIN = 'Arial';
      const FONT_FAMILY_HEADINGS = 'Arial Black';

      const ACCENT_COLOR_PRIMARY = '2E7D32';
      const HEADER_FILL = 'D3D3D3';
      const SALDO_FILL = 'E9ECEF';
      const TEXT_COLOR_LIGHT = 'FFFFFF';
      const TEXT_COLOR_DARK = '333333';
      const BORDER_COLOR_DARK = '000000';
      const ALT_ROW_FILL = 'F8F9FA';
      const POSITIVE_VALUE = '008000';
      const NEGATIVE_VALUE = 'C00000';

      const baseCellStyle: Partial<ExcelJS.Style> = {
        font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: TEXT_COLOR_DARK } },
        alignment: { vertical: 'middle', horizontal: 'left', wrapText: true },
        border: {
          top: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
          bottom: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
          left: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
          right: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
        },
      };

      const titleStyle: Partial<ExcelJS.Style> = {
        font: { name: FONT_FAMILY_HEADINGS, size: 16, bold: true, color: { argb: ACCENT_COLOR_PRIMARY } },
        alignment: { horizontal: 'left', vertical: 'middle' },
      };

      const headerStyle: Partial<ExcelJS.Style> = {
        font: { name: FONT_FAMILY_MAIN, size: 10, bold: true, color: { argb: TEXT_COLOR_DARK } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
          bottom: { style: 'medium', color: { argb: BORDER_COLOR_DARK } },
          left: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
          right: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
        },
      };

      const accountInfoLabelStyle: Partial<ExcelJS.Style> = {
        font: { name: FONT_FAMILY_MAIN, size: 10, bold: true, color: { argb: TEXT_COLOR_DARK } },
        alignment: { horizontal: 'left' }
      };

      const accountInfoValueStyle: Partial<ExcelJS.Style> = {
        font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: TEXT_COLOR_DARK } },
        alignment: { horizontal: 'right' }
      };

      const currencyCellStyle: Partial<ExcelJS.Style> = {
        ...baseCellStyle,
        numFmt: '€#,##0.00;-€#,##0.00;€0.00',
        alignment: { ...baseCellStyle.alignment, horizontal: 'right' },
      };

      const dateCellStyle: Partial<ExcelJS.Style> = {
        ...baseCellStyle,
        numFmt: 'dd.mm.yyyy',
      };

      const textCellStyle: Partial<ExcelJS.Style> = {
        ...baseCellStyle,
        alignment: { ...baseCellStyle.alignment, wrapText: true, vertical: 'top' },
      };

      const saldoCellStyle: Partial<ExcelJS.Style> = {
        ...baseCellStyle,
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: SALDO_FILL } },
        numFmt: '€#,##0.00;-€#,##0.00;€0.00',
        alignment: { ...baseCellStyle.alignment, horizontal: 'right' },
      };

      const totalRowStyleBase: Partial<ExcelJS.Style> = {
        font: { ...baseCellStyle.font, bold: true, size: 11 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E9ECEF' } },
        border: { ...baseCellStyle.border, top: { style: 'medium', color: { argb: BORDER_COLOR_DARK } } },
      };

      const totalRowCurrencyStyle: Partial<ExcelJS.Style> = { ...currencyCellStyle, ...totalRowStyleBase };
      const totalRowTextStyle: Partial<ExcelJS.Style> = { ...textCellStyle, ...totalRowStyleBase };
      const totalRowSaldoStyle: Partial<ExcelJS.Style> = { ...saldoCellStyle, ...totalRowStyleBase };

      // 1. Kontenstatus-Tabelle erstellen
      const statusData = accounts
        .filter((account) => account.id !== 'all')
        .map((account) => {
          const accountTxs = allStatementsSorted.filter(
            (tx) => tx.konto_id === account.id && new Date(tx.buchungstag).getFullYear() === parseInt(selectedYear)
          );
          const einnahmen = accountTxs
            .filter((tx) => tx.betrag > 0)
            .reduce((sum, tx) => sum + (tx.betrag || 0), 0);
          const ausgaben = accountTxs
            .filter((tx) => tx.betrag < 0)
            .reduce((sum, tx) => sum + Math.abs(tx.betrag || 0), 0);
          const bilanz = einnahmen - ausgaben;
          return {
            Kontoname: account.name,
            Einnahmen: einnahmen || null,
            Ausgaben: ausgaben || null,
            Bilanz: bilanz,
          };
        }).filter(row => row.Einnahmen || row.Ausgaben);

      const totalEinnahmen = statusData.reduce((sum, row) => sum + (row.Einnahmen || 0), 0);
      const totalAusgaben = statusData.reduce((sum, row) => sum + (row.Ausgaben || 0), 0);
      const totalBilanz = totalEinnahmen - totalAusgaben;

      if (statusData.length > 0) {
        statusData.push({
          Kontoname: 'Gesamt',
          Einnahmen: totalEinnahmen || null,
          Ausgaben: totalAusgaben || null,
          Bilanz: totalBilanz,
        });

        const statusWorksheet = workbook.addWorksheet('Kontenstatus');
        statusWorksheet.getRow(1).getCell(1).value = `Kontenstatus - ${selectedYear}`;
        statusWorksheet.getRow(1).getCell(1).style = titleStyle;
        
        statusWorksheet.mergeCells(11, 1, 11, 3);
        statusWorksheet.getRow(11).getCell(1).value = `Gesamteinnahmen des Jahres ${selectedYear} (einschließlich Beiträge, Spenden, Zuschüsse, Einnahmen aus der Vermögensverwaltung und aus wirtschaftlichen Betätigungen, Umsatzsteuer)`;
        statusWorksheet.getRow(11).getCell(1).style = {
          font: { name: FONT_FAMILY_MAIN, size: 12, color: { argb: TEXT_COLOR_DARK } },
          alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
          border: {
            top: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            left: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            bottom: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            right: { style: 'thick', color: { argb: BORDER_COLOR_DARK } }
          }
        };
        statusWorksheet.getRow(11).height = 60;
        
        // Fix border for cells 2 and 3 in the merged range
        statusWorksheet.getRow(11).getCell(2).style = {
          border: {
            top: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            bottom: { style: 'thick', color: { argb: BORDER_COLOR_DARK } }
          }
        };
        statusWorksheet.getRow(11).getCell(3).style = {
          border: {
            top: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            bottom: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            right: { style: 'thick', color: { argb: BORDER_COLOR_DARK } }
          }
        };

        statusWorksheet.getRow(11).getCell(4).value = totalBilanz > 0 ? Math.round(totalBilanz) : 0;
        statusWorksheet.getRow(11).getCell(4).style = {
          ...currencyCellStyle,
          font: { ...currencyCellStyle.font, size: 16, bold: true, color: { argb: totalBilanz > 0 ? POSITIVE_VALUE : TEXT_COLOR_DARK } },
          alignment: { ...currencyCellStyle.alignment, vertical: 'middle' },
          border: {
            top: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            left: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            right: { style: 'thick', color: { argb: BORDER_COLOR_DARK } },
            bottom: { style: 'thick', color: { argb: BORDER_COLOR_DARK } }
          }
        };

        const statusHeaders = ['Kontoname', 'Einnahmen', 'Ausgaben', 'Bilanz'];
        statusWorksheet.getRow(4).values = statusHeaders;
        statusWorksheet.getRow(4).height = 20;
        statusHeaders.forEach((_, index) => {
          const cell = statusWorksheet.getRow(4).getCell(index + 1);
          cell.style = headerStyle;
        });

        statusData.forEach((row, rowIndex) => {
          const dataRow = statusWorksheet.getRow(rowIndex + 5);
          dataRow.height = 20;
          dataRow.values = [row.Kontoname, row.Einnahmen, row.Ausgaben, row.Bilanz];
          const isTotalRow = row.Kontoname === 'Gesamt';
          const rowFill: any = rowIndex % 2 === 0 
            ? { type: 'pattern', pattern: 'solid', fgColor: { argb: TEXT_COLOR_LIGHT } } 
            : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };

          dataRow.getCell(1).style = isTotalRow ? { ...totalRowTextStyle, fill: rowFill } : { ...textCellStyle, fill: rowFill };
          dataRow.getCell(2).style = isTotalRow
            ? { ...totalRowCurrencyStyle, fill: rowFill, font: { ...totalRowCurrencyStyle.font, color: { argb: row.Einnahmen ? POSITIVE_VALUE : TEXT_COLOR_DARK } } }
            : { ...currencyCellStyle, fill: rowFill, font: { ...currencyCellStyle.font, color: { argb: row.Einnahmen ? POSITIVE_VALUE : TEXT_COLOR_DARK } } };
          dataRow.getCell(3).style = isTotalRow
            ? { ...totalRowCurrencyStyle, fill: rowFill, font: { ...totalRowCurrencyStyle.font, color: { argb: row.Ausgaben ? NEGATIVE_VALUE : TEXT_COLOR_DARK } } }
            : { ...currencyCellStyle, fill: rowFill, font: { ...currencyCellStyle.font, color: { argb: row.Ausgaben ? NEGATIVE_VALUE : TEXT_COLOR_DARK } } };
          const bilanzStyle = row.Bilanz > 0 ? { color: { argb: POSITIVE_VALUE } } : (row.Bilanz < 0 ? { color: { argb: NEGATIVE_VALUE } } : {});
          dataRow.getCell(4).style = isTotalRow
            ? { ...totalRowCurrencyStyle, fill: rowFill, font: { ...totalRowCurrencyStyle.font, ...bilanzStyle } }
            : { ...currencyCellStyle, fill: rowFill, font: { ...currencyCellStyle.font, ...bilanzStyle } };
        });

        statusWorksheet.columns = [
          { width: 35 },
          { width: 20 },
          { width: 20 },
          { width: 20 },
        ];
        statusWorksheet.properties.defaultRowHeight = 15;
        statusWorksheet.pageSetup = { paperSize: 9, orientation: 'landscape' };
      }

      // 2. Buchungen-Tabellen für jedes Konto
      accounts.forEach((account) => {
        if (account.id === 'all') return;

        const accountStatementsThisYear = allStatementsSorted.filter(
          (tx) => tx.konto_id === account.id && new Date(tx.buchungstag).getFullYear() === parseInt(selectedYear)
        );

        if (accountStatementsThisYear.length === 0) return;

        const firstDayOfYear = new Date(parseInt(selectedYear), 0, 1);
        const statementsBeforeThisYear = allStatementsSorted.filter(
          s => s.konto_id === account.id && new Date(s.buchungstag) < firstDayOfYear
        );
        const startSaldoForYear = statementsBeforeThisYear.reduce((sum, stmt) => sum + (stmt.betrag || 0), 0);

        const accountInfoRows = [
          [`${account.name} - ${selectedYear}`],
          [],
          ['Kontotyp:', '', '', '', '', account.kontotyp || 'Sonstiges'],
          ['Beschreibung:', '', '', '', '', account.beschreibung || '-'],
          ['Währung:', '', '', '', '', account.waehrung || 'EUR'],
          ['Startsaldo (Anfang ' + selectedYear + '):', '', '', '', '', startSaldoForYear],
          [],
        ];

        const cleanSheetName = account.name.replace(/[\\/*?[\]:]/g, "").slice(0, 31);
        const worksheet = workbook.addWorksheet(cleanSheetName);

        accountInfoRows.forEach((row, index) => {
          const rowNum = index + 1;
          if (index === 0) {
            worksheet.getRow(rowNum).getCell(1).value = row[0];
            worksheet.getRow(rowNum).getCell(1).style = titleStyle;
          } else if (index >= 2 && index <= 5) {
            worksheet.mergeCells(rowNum, 1, rowNum, 2);
            worksheet.getRow(rowNum).getCell(1).value = row[0];
            worksheet.getRow(rowNum).getCell(1).style = accountInfoLabelStyle;

            worksheet.mergeCells(rowNum, 5, rowNum, 7);
            const valueCell = worksheet.getRow(rowNum).getCell(5);
            valueCell.value = row[5];
            if (row[5] !== undefined) {
              if (typeof row[0] === 'string' && row[0].toLowerCase().includes('startsaldo')) {
                valueCell.style = {
                  ...currencyCellStyle,
                  font: { ...currencyCellStyle.font, bold: true },
                  alignment: { ...currencyCellStyle.alignment, horizontal: 'right' },
                  border: {}
                };
              } else {
                valueCell.style = {
                  ...accountInfoValueStyle,
                  alignment: { ...accountInfoValueStyle.alignment, horizontal: 'right' }
                };
              }
            }
          }
        });

        const accountTxsForSheet = accountStatementsThisYear.map((tx) => ({
          OriginalId: tx.originalId,
          HasDocuments: (tx.documents || []).length > 0,
          Documents: tx.documents || [],
          Datum: new Date(tx.buchungstag),
          Buchungstext: tx.buchungstext || '-',
          Zahlungsbeteiligter: tx.name_zahlungsbeteiligter || '-',
          Verwendungszweck: tx.verwendungszweck || '-',
          Einnahmen: tx.betrag > 0 ? tx.betrag : null,
          Ausgaben: tx.betrag < 0 ? Math.abs(tx.betrag) : null,
          Saldo: startSaldoForYear + accountStatementsThisYear
            .filter(sInternal => new Date(sInternal.buchungstag) <= new Date(tx.buchungstag) &&
              (new Date(sInternal.buchungstag).getTime() !== new Date(tx.buchungstag).getTime() ||
                (parseInt(sInternal.originalId || '0', 10) || sInternal.originalId || 0) <= (parseInt(tx.originalId || '0', 10) || tx.originalId || 0)
              ))
            .reduce((sum, sCurr) => sum + (sCurr.betrag || 0), 0)
        }));

        const sumEinnahmen = accountTxsForSheet.reduce((sum, tx) => sum + (tx.Einnahmen || 0), 0);
        const sumAusgaben = accountTxsForSheet.reduce((sum, tx) => sum + (tx.Ausgaben || 0), 0);
        accountTxsForSheet.push({
          OriginalId: '',
          HasDocuments: false,
          Documents: [],
          Datum: null as any,
          Buchungstext: 'Summen',
          Zahlungsbeteiligter: '',
          Verwendungszweck: '',
          Einnahmen: sumEinnahmen,
          Ausgaben: sumAusgaben,
          Saldo: null as any
        });

        const headerRowActual = accountInfoRows.length + 1;
        const txHeaders = ['Datum', 'Buchungstext', 'Zahlungsbeteiligter', 'Verwendungszweck', 'Einnahmen', 'Ausgaben', 'Saldo'];
        worksheet.getRow(headerRowActual).values = txHeaders;
        txHeaders.forEach((_, index) => {
          worksheet.getRow(headerRowActual).getCell(index + 1).style = headerStyle;
        });

        accountTxsForSheet.forEach((tx, rowIndex) => {
          const dataRowNum = headerRowActual + rowIndex + 1;
          const dataRow = worksheet.getRow(dataRowNum);
          dataRow.values = [
            tx.Datum,
            tx.Buchungstext,
            tx.Zahlungsbeteiligter,
            tx.Verwendungszweck,
            tx.Einnahmen,
            tx.Ausgaben,
            tx.Saldo
          ];
          const isTotalRow = rowIndex === accountTxsForSheet.length - 1;
          const rowFill: any = isTotalRow ? {} : { type: 'pattern', pattern: 'solid', fgColor: { argb: rowIndex % 2 === 0 ? TEXT_COLOR_LIGHT : ALT_ROW_FILL } };

          dataRow.getCell(1).style = isTotalRow ? totalRowTextStyle : { ...dateCellStyle, fill: rowFill };
          [2, 3].forEach(colIdx => {
            dataRow.getCell(colIdx).style = isTotalRow ? totalRowTextStyle : { ...textCellStyle, fill: rowFill };
          });

          const verwendungszweckCell = dataRow.getCell(4);
          if (includeDocumentHyperlinks && tx.HasDocuments && !isTotalRow && tx.OriginalId !== 'start') {
            // Verwende das erste Dokument für den Hyperlink (falls vorhanden)
            const firstDoc = tx.Documents && tx.Documents.length > 0 ? tx.Documents[0] : null;
            if (firstDoc && firstDoc.file_path) {
              verwendungszweckCell.value = {
                text: tx.Verwendungszweck,
                hyperlink: `file://${firstDoc.file_path}`,
                tooltip: firstDoc.description || `Dokument für Buchung ${tx.OriginalId} anzeigen`
              };
              verwendungszweckCell.style = {
                ...textCellStyle,
                font: { ...textCellStyle.font, color: { argb: '0000FF' }, underline: true },
                fill: rowFill
              };
            } else {
              // Fallback wenn kein file_path vorhanden - verwende generischen Link
              verwendungszweckCell.value = {
                text: tx.Verwendungszweck,
                hyperlink: `http://localhost:3000/documents/${tx.OriginalId}`,
                tooltip: `Dokument für Buchung ${tx.OriginalId} anzeigen`
              };
              verwendungszweckCell.style = {
                ...textCellStyle,
                font: { ...textCellStyle.font, color: { argb: '0000FF' }, underline: true },
                fill: rowFill
              };
            }
          } else {
            verwendungszweckCell.value = tx.Verwendungszweck;
            verwendungszweckCell.style = isTotalRow ? totalRowTextStyle : { ...textCellStyle, fill: rowFill };
          }

          dataRow.getCell(5).style = isTotalRow
            ? { ...totalRowCurrencyStyle, font: { ...totalRowCurrencyStyle.font, color: { argb: tx.Einnahmen ? POSITIVE_VALUE : TEXT_COLOR_DARK } } }
            : { ...currencyCellStyle, fill: rowFill, font: { ...currencyCellStyle.font, color: { argb: tx.Einnahmen ? POSITIVE_VALUE : TEXT_COLOR_DARK } } };
          dataRow.getCell(6).style = isTotalRow
            ? { ...totalRowCurrencyStyle, font: { ...totalRowCurrencyStyle.font, color: { argb: tx.Ausgaben ? NEGATIVE_VALUE : TEXT_COLOR_DARK } } }
            : { ...currencyCellStyle, fill: rowFill, font: { ...currencyCellStyle.font, color: { argb: tx.Ausgaben ? NEGATIVE_VALUE : TEXT_COLOR_DARK } } };
          const saldoStyle = tx.Saldo && tx.Saldo > 0 ? { color: { argb: POSITIVE_VALUE } } : (tx.Saldo && tx.Saldo < 0 ? { color: { argb: NEGATIVE_VALUE } } : {});
          dataRow.getCell(7).style = isTotalRow
            ? { ...totalRowSaldoStyle }
            : { ...saldoCellStyle, fill: rowFill, font: { ...saldoCellStyle.font, ...saldoStyle } };
        });

        worksheet.columns = [
          { width: 10 },
          { width: 15 },
          { width: 19 },
          { width: 48 },
          { width: 10 },
          { width: 10 },
          { width: 10 },
        ];
        worksheet.properties.defaultRowHeight = 15;
        worksheet.pageSetup = { paperSize: 9, orientation: 'landscape' };
        worksheet.views = [{ state: 'frozen', ySplit: headerRowActual }];
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${selectedYear}_Kontoauszug.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      // Success feedback
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Export erfolgreich',
        message: `Die Excel-Datei wurde erfolgreich erstellt und heruntergeladen.`,
        variant: 'single'
      });
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Export error:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Export fehlgeschlagen',
        message: 'Beim Erstellen der Excel-Datei ist ein Fehler aufgetreten.',
        variant: 'single'
      });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (isOpen && accounts.length > 0 && !selectedModalAccount) {
      const firstAccount = accounts.find((acc) => acc.id !== 'all');
      setSelectedModalAccount(firstAccount?.id || null);
    }
  }, [isOpen, accounts, selectedModalAccount]);

  const handleClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalShell
        open={isOpen}
        onClose={handleClose}
        variant="form"
        size="xl"
        icon={DocumentArrowDownIcon}
        title="Excel-Export"
        subtitle="Kontoauszüge exportieren"
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: onClose, disabled: isExporting }}
            primary={{
              label: isExporting ? 'Exportiere...' : 'Excel exportieren',
              onClick: exportToExcel,
              disabled: isLoading || !selectedYear || accounts.filter(acc => acc.id !== 'all').length === 0 || isExporting,
              loading: isExporting,
              icon: <DocumentArrowDownIcon className="h-5 w-5" />,
            }}
          />
        }
      >
        {/* Loading Overlay */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-primary-200 dark:border-primary-800 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <p className="mt-6 text-lg font-medium text-gray-700 dark:text-gray-300">
              Lade Buchungsdaten...
            </p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Bitte warten Sie einen Moment
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Settings Section */}
            <ModalSection title="Einstellungen">
              <div className="space-y-4">
                {/* Year and Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <AttributeLabel iconKey="year" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Geschäftsjahr
                    </AttributeLabel>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="w-full p-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
                    >
                      {availableYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col justify-end">
                    <label className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <input
                        type="checkbox"
                        checked={includeDocumentHyperlinks}
                        onChange={(e) => setIncludeDocumentHyperlinks(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Dokumenten-Links einfügen
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Für Kassenprüfung erforderlich
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Statistics Cards */}
                {selectedModalAccount && accountStats.anzahl > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-gray-50 dark:bg-[#0f1419] rounded-xl p-3 border border-gray-200 dark:border-gray-700/50">
                      <AttributeLabel iconKey="income" size="sm" className="block mb-1" textClassName="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Einnahmen</AttributeLabel>
                      <div className="flex items-center justify-between">
                        <p className="text-sm sm:text-base font-bold text-success-600 dark:text-success-400">
                          {isMobile ? formatCurrencyCompact(accountStats.einnahmen) : formatCurrency(accountStats.einnahmen)}
                        </p>
                        <CheckCircleIcon className="h-4 w-4 text-success-500" />
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#0f1419] rounded-xl p-3 border border-gray-200 dark:border-gray-700/50">
                      <AttributeLabel iconKey="expense" size="sm" className="block mb-1" textClassName="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ausgaben</AttributeLabel>
                      <div className="flex items-center justify-between">
                        <p className="text-sm sm:text-base font-bold text-error-600 dark:text-error-400">
                          {isMobile ? formatCurrencyCompact(accountStats.ausgaben) : formatCurrency(accountStats.ausgaben)}
                        </p>
                        <XMarkIcon className="h-4 w-4 text-error-500" />
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#0f1419] rounded-xl p-3 border border-gray-200 dark:border-gray-700/50">
                      <AttributeLabel iconKey="balance" size="sm" className="block mb-1" textClassName="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Bilanz</AttributeLabel>
                      <div className="flex items-center justify-between">
                        <p className={`text-sm sm:text-base font-bold ${accountStats.bilanz >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                          {isMobile ? formatCurrencyCompact(accountStats.bilanz) : formatCurrency(accountStats.bilanz)}
                        </p>
                        <WalletIcon className="h-4 w-4 text-primary-500" />
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#0f1419] rounded-xl p-3 border border-gray-200 dark:border-gray-700/50">
                      <AttributeLabel iconKey="count" size="sm" className="block mb-1" textClassName="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Buchungen</AttributeLabel>
                      <div className="flex items-center justify-between">
                        <p className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
                          {accountStats.anzahl}
                        </p>
                        <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ModalSection>

            {/* Account Tabs */}
            <ModalSection title="Konto">
              <div className="flex overflow-x-auto scrollbar-hide -mx-1 px-1 border-b border-gray-200 dark:border-gray-700">
                {accounts
                  .filter((account) => account.id !== 'all')
                  .map((account) => (
                    <button
                      key={account.id}
                      onClick={() => setSelectedModalAccount(account.id)}
                      className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-all duration-200 ${
                        selectedModalAccount === account.id
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {account.name}
                    </button>
                  ))}
              </div>
            </ModalSection>

            {/* Transactions Preview */}
            <ModalSection title="Buchungsvorschau">
              {selectedModalAccount ? (
                filteredModalStatementsForYear.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Datum</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 hidden sm:table-cell">Buchungstext</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Beteiligter</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 hidden lg:table-cell">Verwendung</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">Betrag</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300 hidden sm:table-cell">Saldo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredModalStatementsForYear.slice(0, 10).map((statement, index) => (
                          <tr
                            key={statement.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">
                              {formatDateForDisplay(statement.buchungstag)}
                            </td>
                            <td className="px-3 py-2 text-gray-900 dark:text-white hidden sm:table-cell">
                              {statement.buchungstext}
                            </td>
                            <td className="px-3 py-2 text-gray-900 dark:text-white truncate max-w-[150px]">
                              {statement.name_zahlungsbeteiligter || '-'}
                            </td>
                            <td className="px-3 py-2 text-gray-900 dark:text-white hidden lg:table-cell truncate max-w-[200px]">
                              {statement.verwendungszweck || '-'}
                            </td>
                            <td className={`px-3 py-2 text-right font-medium ${
                              statement.betrag < 0 ? 'text-error-600 dark:text-error-400' : 'text-success-600 dark:text-success-400'
                            }`}>
                              {isMobile ? formatCurrencyCompact(statement.betrag) : formatCurrency(statement.betrag)}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-white hidden sm:table-cell">
                              {isMobile ? formatCurrencyCompact(statement.saldo) : formatCurrency(statement.saldo)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredModalStatementsForYear.length > 10 && (
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
                        ... und {filteredModalStatementsForYear.length - 10} weitere Buchungen
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      Keine Buchungen für dieses Konto in {selectedYear} vorhanden.
                    </p>
                  </div>
                )
              ) : (
                <div className="text-center py-8">
                  <WalletIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Wählen Sie ein Konto aus, um die Buchungen anzuzeigen.
                  </p>
                </div>
              )}
            </ModalSection>
          </div>
        )}
      </ModalShell>

      {/* Success/Error Feedback */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={{
          text: 'OK',
          onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        }}
      />
    </>
  );
};

export default ExcelExportModal;